import { toPersianDigits } from "../../utils/exhibitionUtils";
import LoadingIndicator from "../Atomics/Loading/Loading";

// Helper function to wrap text based on store dimensions
const wrapStoreName = (storeName, storeWidth, storeHeight) => {
  const fontSize = Math.max(8, Math.min(12, storeWidth / 8));
  const lineHeight = fontSize + 2;
  const maxWidth = storeWidth - 8; // Leave some padding
  const maxHeight = storeHeight - 20; // Leave space for store number
  const maxLines = Math.floor(maxHeight / lineHeight);

  // If we can only fit one line, use simple truncation
  if (maxLines <= 1) {
    const maxChars = Math.floor(storeWidth / 6);
    return storeName.length > maxChars
      ? [{ text: storeName.substring(0, maxChars - 3) + "...", fontSize }]
      : [{ text: storeName, fontSize }];
  }

  // Split text into words
  const words = storeName.split(" ");
  const lines = [];
  let currentLine = "";

  // Text wrapping algorithm
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const estimatedWidth = testLine.length * (fontSize * 0.6); // Rough character width estimation

    if (estimatedWidth <= maxWidth && lines.length < maxLines) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is too long, truncate it
        const maxChars = Math.floor(maxWidth / (fontSize * 0.6));
        const truncated =
          word.length > maxChars
            ? word.substring(0, maxChars - 3) + "..."
            : word;
        lines.push(truncated);
        currentLine = "";
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  // If we have too many lines, truncate the last line and add ellipsis
  if (lines.length > maxLines) {
    lines.splice(maxLines - 1);
    if (lines[maxLines - 2]) {
      const lastLine = lines[maxLines - 2];
      const maxChars = Math.floor(maxWidth / (fontSize * 0.6));
      lines[maxLines - 2] =
        lastLine.length > maxChars - 3
          ? lastLine.substring(0, maxChars - 6) + "..."
          : lastLine + "...";
    }
  }

  return lines.map((line) => ({ text: line, fontSize }));
};

export default function FloorMap({
  floor,
  onStoreClick,
  selectedStoreId,
  stores,
}) {
  return (
    <svg
      className="exhibition-floor-map"
      viewBox="0 0 900 600"
      preserveAspectRatio="xMidYMid meet"
      style={{ background: "var(--site-theme)" }}
    >
      <rect
        x="22"
        y="22"
        width="856"
        height="556"
        fill="none"
        stroke="#1e2939"
        strokeWidth="2"
        rx="7"
        ry="7"
      />

      {/* Floor entrance */}
      {floor?.entrance_x && floor?.entrance_y && (
        <>
          {floor.entrance_position === "top" && (
            <g>
              <rect
                fill="var(--site-theme)"
                x={floor.entrance_x}
                y={floor.entrance_y}
                width={60}
                height={4}
              />
              <rect
                fill="#1e2939"
                x={floor.entrance_x}
                y={floor.entrance_y}
                width={62}
                height={2}
                transform={`rotate(-20, ${floor.entrance_x + 2}, ${
                  floor.entrance_y + 2
                })`}
                rx="2"
                ry="2"
              />
              <text
                x={floor.entrance_x + 35}
                y={floor.entrance_y + 3}
                textAnchor="middle"
                className="store-text"
                fill="#1f2937"
                fontSize="11"
                fontWeight="700"
              >
                درب ورودی
              </text>
            </g>
          )}
          {floor.entrance_position === "left" && (
            <g>
              <rect
                x={floor.entrance_x}
                y={floor.entrance_y - 22}
                width={60}
                height={5}
                transform={`rotate(90, ${floor.entrance_x + 0.5}, ${
                  floor.entrance_y + -2
                })`}
                fill="var(--site-theme)"
              />
              <rect
                fill="#1e2939"
                x={floor.entrance_x - 4}
                y={floor.entrance_y - 21}
                width={62}
                height={2}
                transform={`rotate(110, ${floor.entrance_x + 1.5}, ${
                  floor.entrance_y + -4
                })`}
                rx="2"
                ry="2"
              />
              <text
                x={floor.entrance_x + 29}
                y={floor.entrance_y + 8}
                textAnchor="middle"
                fill="#1f2937"
                fontSize="11"
                fontWeight="700"
                transform={`rotate(90, ${floor.entrance_x + 12}, ${
                  floor.entrance_y + 14
                })`}
              >
                درب ورودی
              </text>
            </g>
          )}
          {floor.entrance_position === "bottom" && (
            <g>
              <rect
                x={floor.entrance_x}
                y={floor.entrance_y}
                width={60}
                height={5}
                fill="var(--site-theme)"
              />
              <rect
                fill="#1e2939"
                x={floor.entrance_x - 1}
                y={floor.entrance_y + 2}
                width={62}
                height={2}
                transform={`rotate(20, ${floor.entrance_x + 2}, ${
                  floor.entrance_y + 3.5
                })`}
                rx="2"
                ry="2"
              />
              <text
                x={floor.entrance_x + 34}
                y={floor.entrance_y + 2}
                textAnchor="middle"
                fill="#1f2937"
                fontSize="11"
                fontWeight="700"
              >
                درب ورودی
              </text>
            </g>
          )}
          {floor.entrance_position === "right" && (
            <g>
              <rect
                x={floor.entrance_x}
                y={floor.entrance_y}
                width={60}
                height={5}
                transform={`rotate(90, ${floor.entrance_x + 0.5}, ${
                  floor.entrance_y + -2
                })`}
                fill="var(--site-theme)"
              />
              <rect
                fill="#1e2939"
                x={floor.entrance_x - 1}
                y={floor.entrance_y + 1}
                width={62}
                height={2}
                transform={`rotate(70, ${floor.entrance_x + 0.5}, ${
                  floor.entrance_y + -2.5
                })`}
                rx="2"
                ry="2"
              />
              <text
                x={floor.entrance_x - 21}
                y={floor.entrance_y + 24}
                textAnchor="middle"
                fill="#1f2937"
                fontSize="11"
                fontWeight="700"
                transform={`rotate(-90, ${floor.entrance_x - 10}, ${
                  floor.entrance_y + 18
                })`}
              >
                درب ورودی
              </text>
            </g>
          )}
        </>
      )}
      {stores?.map((store) => {
        const displayIdRaw = store.store_id_number ?? store.id;
        const displayId = Number.isFinite(displayIdRaw)
          ? displayIdRaw
          : Number(displayIdRaw);
        const storeKey = store.id ?? store.store_id_number ?? Math.random();
        const isSelected =
          selectedStoreId === (store.id ?? store.store_id_number);
        const hoverTitle =
          displayId < 1000 &&
          `${store.store_name ?? ""} — غرفه ${
            displayId !== undefined ? toPersianDigits(displayId) : ""
          }${
            store.store_name && store.store_name.length > 20
              ? "\n(کلیک برای مشاهده جزئیات کامل)"
              : ""
          }`;

        return (
          <g key={storeKey}>
            <title>{hoverTitle}</title>
            <defs>
              <clipPath id={`clip-${storeKey}`}>
                <rect
                  x={store.start_x + 2}
                  y={store.start_y + 2}
                  width={Math.max(0, store.width - 4)}
                  height={Math.max(0, store.height - 4)}
                />
              </clipPath>
            </defs>
            <rect
              x={store.start_x}
              y={store.start_y}
              width={store.width}
              height={store.height}
              fill={displayId < 1000 ? "#ffffff" : "#cfcfcfff"}
              stroke="#1f2937"
              strokeWidth={isSelected ? 3 : 2}
              className="exhibition-store-rect"
              style={{ cursor: displayId < 1000 ? "pointer" : "default" }}
              onClick={() =>
                onStoreClick && displayId < 1000 && onStoreClick(store)
              }
              title={hoverTitle}
            />
            {/* Store number */}
            {displayId < 1000 && (
              <text
                x={store.start_x + store.width / 2}
                y={store.start_y + store.height / 2 - 25}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#1f2937"
                fontWeight="700"
                onClick={() => onStoreClick && onStoreClick(store)}
                title={hoverTitle}
              >
                {displayId !== undefined ? toPersianDigits(displayId) : ""}
              </text>
            )}

            {/* Store name with text wrapping */}
            {store.width > 40 && store.height > 25 && store.store_name && (
              <g clipPath={`url(#clip-${storeKey})`}>
                {(() => {
                  const wrappedLines = wrapStoreName(
                    store.store_name,
                    store.width,
                    store.height
                  );
                  const lineHeight = wrappedLines[0]?.fontSize + 2 || 10;
                  const totalTextHeight = wrappedLines.length * lineHeight;
                  const startY =
                    store.start_y + store.height - 8 - totalTextHeight / 2;

                  return wrappedLines.map((line, index) => (
                    <text
                      key={`${storeKey}-line-${index}`}
                      x={store.start_x + store.width / 2}
                      y={
                        displayId < 1000
                          ? startY + index * lineHeight - 20
                          : store.start_y +
                            store.height / 2 -
                            totalTextHeight / 2 +
                            index * lineHeight +
                            7
                      }
                      textAnchor="middle"
                      dominantBaseline="auto"
                      fill="#374151"
                      fontWeight="600"
                      fontSize={line.fontSize}
                      style={{ pointerEvents: "none" }}
                    >
                      {line.text}
                    </text>
                  ));
                })()}
              </g>
            )}
            {/* Store entrance indicators */}
            {store.entrance === "top" && (
              <rect
                x={store.start_x + store.width / 2 - 15}
                y={store.start_y - 2}
                width="30"
                height="4"
                fill="var(--site-theme)"
              />
            )}
            {store.entrance === "bottom" && (
              <rect
                x={store.start_x + store.width / 2 - 15}
                y={store.start_y + store.height - 2}
                width="30"
                height="4"
                fill="var(--site-theme)"
              />
            )}
            {store.entrance === "left" && (
              <rect
                x={store.start_x - 2}
                y={store.start_y + store.height / 2 - 15}
                width="4"
                height="30"
                fill="var(--site-theme)"
              />
            )}
            {store.entrance === "right" && (
              <rect
                x={store.start_x + store.width - 2}
                y={store.start_y + store.height / 2 - 15}
                width="4"
                height="30"
                fill="var(--site-theme)"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
